import { useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, MessageCircle } from "lucide-react";
import { useSite, UI } from "@/lib/site";
import { Logo } from "@/components/Logo";
import { InstaButton } from "@/components/WaButton";
import { useLenis, scrollToId } from "@/components/SmoothScroll";

const DIRS = ["events", "production", "marketing", "booking"];

const LangSwitch = ({ testPrefix }) => {
  const { lang, setLang, langs } = useSite();
  return (
    <div className="flex items-center rounded-full border border-[#b8893b]/35 p-0.5 text-[0.7rem] font-semibold">
      {langs.map((l) => (
        <button
          key={l}
          onClick={() => setLang(l)}
          data-testid={`${testPrefix}-lang-${l}`}
          className={`rounded-full px-2.5 py-1 uppercase tracking-wider transition-colors duration-300 ${lang === l ? "bg-[#1d1712] text-[#f6ecd9]" : "text-[#5b4f43] hover:text-[#1d1712]"}`}
        >
          {l === "ky" ? "KG" : l}
        </button>
      ))}
    </div>
  );
};

export const Header = () => {
  const { t, wa } = useSite();
  const lenis = useLenis();
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const on = () => setScrolled(window.scrollY > 40);
    on();
    window.addEventListener("scroll", on, { passive: true });
    return () => window.removeEventListener("scroll", on);
  }, []);
  useEffect(() => { setOpen(false); }, [pathname]);

  const goContacts = () => {
    setOpen(false);
    if (pathname === "/") scrollToId(lenis, "contacts");
    else navigate("/#contacts");
  };

  const linkCls = (active) => `relative py-1 text-[0.82rem] font-medium tracking-wide transition-colors duration-300 ${active ? "text-[#8a6424]" : "text-[#2a2119] hover:text-[#8a6424]"}`;

  return (
    <>
      <header
        data-testid="site-header"
        className={`fixed inset-x-0 top-0 z-50 transition-[background-color,box-shadow,padding] duration-500 ${scrolled ? "bg-[#fbf7ef]/80 py-3 shadow-[0_8px_30px_-20px_rgba(60,40,10,0.35)] backdrop-blur-xl" : "py-5"}`}
      >
        <div className="mx-auto flex max-w-[1400px] items-center justify-between px-5 md:px-10">
          <Link to="/" data-testid="header-logo-link"><Logo /></Link>
          <nav className="hidden items-center gap-8 lg:flex">
            {DIRS.map((d) => (
              <Link key={d} to={`/direction/${d}`} className={linkCls(pathname === `/direction/${d}`)} data-testid={`nav-${d}`}>
                {t(UI.nav[d])}
              </Link>
            ))}
            <button onClick={goContacts} className={linkCls(false)} data-testid="nav-contacts">{t(UI.nav.contacts)}</button>
          </nav>
          <div className="flex items-center gap-3">
            <div className="hidden sm:block"><LangSwitch testPrefix="header" /></div>
            <InstaButton iconOnly className="hidden md:inline-flex" testId="header-instagram-btn" />
            <a href={wa()} target="_blank" rel="noopener noreferrer" className="btn-gold hidden !px-5 !py-2.5 md:inline-flex" data-testid="header-whatsapp-btn">
              <MessageCircle className="h-4 w-4" strokeWidth={1.8} />
              <span>{t(UI.contact)}</span>
            </a>
            <button className="grid h-11 w-11 place-items-center rounded-full border border-[#b8893b]/40 lg:hidden" onClick={() => setOpen(true)} data-testid="mobile-menu-open" aria-label="menu">
              <Menu className="h-5 w-5" />
            </button>
          </div>
        </div>
      </header>

      <AnimatePresence>
        {open && (
          <motion.div
            className="fixed inset-0 z-[60] flex flex-col bg-[#fbf7ef] px-6 pb-10 pt-5"
            initial={{ clipPath: "circle(0% at 95% 4%)" }}
            animate={{ clipPath: "circle(150% at 95% 4%)" }}
            exit={{ clipPath: "circle(0% at 95% 4%)" }}
            transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
            data-testid="mobile-menu"
          >
            <div className="flex items-center justify-between">
              <Logo />
              <button className="grid h-11 w-11 place-items-center rounded-full border border-[#b8893b]/40" onClick={() => setOpen(false)} data-testid="mobile-menu-close" aria-label="close">
                <X className="h-5 w-5" />
              </button>
            </div>
            <nav className="mt-14 flex flex-col gap-2">
              <Link to="/" className="font-display text-4xl text-[#1d1712]" data-testid="mobile-nav-home">{t(UI.home)}</Link>
              {DIRS.map((d, i) => (
                <Link key={d} to={`/direction/${d}`} className="font-display flex items-baseline gap-3 text-4xl text-[#1d1712]" data-testid={`mobile-nav-${d}`}>
                  <span className="text-sm text-[#8a6424]">0{i + 1}</span>{t(UI.nav[d])}
                </Link>
              ))}
              <button onClick={goContacts} className="font-display text-left text-4xl text-[#1d1712]" data-testid="mobile-nav-contacts">{t(UI.nav.contacts)}</button>
            </nav>
            <div className="mt-auto flex flex-col gap-5">
              <LangSwitch testPrefix="mobile" />
              <a href={wa()} target="_blank" rel="noopener noreferrer" className="btn-gold justify-center" data-testid="mobile-whatsapp-btn">
                <MessageCircle className="h-4 w-4" /> {t(UI.writeWa)}
              </a>
              <InstaButton className="justify-center" testId="mobile-instagram-btn" />
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};
